import * as Color from 'color';
import * as React from 'react';

import {defineMessages, useIntl} from '@pinecast/i18n';
import DownChevronMedium from '@pinecast/common/icons/DownChevronMedium';
import {useCSS} from '@pinecast/styles';
import {MOBILE} from './mobile';
import {DEFAULT_FONT} from '@pinecast/common/constants';
import {useTheme} from './ThemeProvider';

const messages = defineMessages({
  label: {
    id: 'pl-spa.SpeedSelector.label',
    description: 'Label for the playback rate selector',
    defaultMessage: 'Playback rate',
  },
});

type Props = {
  audio: HTMLAudioElement;
};

const SpeedSelector = ({audio}: Props) => {
  const css = useCSS();
  const intl = useIntl();

  const theme = useTheme();
  const color = Color(theme.primaryColor).isDark() ? '#ddd' : '#444';

  const [speed, setSpeed] = React.useState(audio.playbackRate);

  React.useEffect(() => {
    audio.playbackRate = speed;
    const rateChange = () => {
      setSpeed(audio.playbackRate);
    };
    audio.addEventListener('ratechange', rateChange);
    return () => {
      audio.removeEventListener('ratechange', rateChange);
    };
  }, [audio]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={css({
        position: 'relative',
      })}
    >
      <select
        aria-label={intl.formatMessage(messages.label)}
        className={css({
          appearance: 'none',
          background: theme.primaryColor,
          border: `1px solid ${color}`,
          color,
          fontFamily: DEFAULT_FONT,
          fontWeight: 400,
          height: '24px',
          lineHeight: '1em',
          padding: '0 16px 0 8px',
          borderRadius: '6px',
          [MOBILE]: {
            height: '20px',
            lineHeight: '20px',
            padding: '0 12px 0 4px',
          },
        })}
        value={speed}
        onChange={e => {
          audio.playbackRate = parseFloat(e.target.value);
        }}
      >
        <option value="1">1×</option>
        <option value="1.5">1.5×</option>
        <option value="2">2×</option>
        <option value="2.5">2.5×</option>
      </select>
      <DownChevronMedium
        size={8}
        style={{
          position: 'absolute',
          right: 8,
          top: 0,
          bottom: 0,
          margin: 'auto',
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      />
    </div>
  );
};

export default SpeedSelector;
